// Layers of an opened HISTORICAL analysis. Every source/layer id starts with
// "hist-", so they live next to the layers of the current analysis without
// ever replacing them (those use their own ids in polygonLayers / impactLayers).
//
// Self-contained on purpose: the colors below are copies of STYLES and
// IMPACT_LAYERS, so this file has no import that could fail to resolve.

const PREFIX = "hist";
const DOT_MAX_ZOOM = 15; // below this zoom, buildings are shown as dots

// drawing order = stacking order (last one is on top)
// "prop" = key in the GET /fire-analysis/{id} response
const SHAPES = [
  {
    id: "real",
    prop: "real_fire",
    fill: "#940909",
    fillOpacity: 0.55,
    line: "#940909",
    lineWidth: 2,
    dasharray: null,
    dot: null,
  },
  {
    id: "zone",
    prop: "saved_zone",
    fill: "#738C32",
    fillOpacity: 0.7,
    line: "#738C32",
    lineWidth: 1.5,
    dasharray: null,
    dot: null,
  },
  {
    id: "sim",
    prop: "simulated_fire",
    fill: "#000000",
    fillOpacity: 0,
    line: "#59112B",
    lineWidth: 3,
    dasharray: [2, 2],
    dot: null,
  },
  {
    id: "saved",
    prop: "saved_buildings",
    fill: "#5a9713",
    fillOpacity: 0.95,
    line: "#3F6B12",
    lineWidth: 1,
    dasharray: null,
    dot: "#5a9713",
  },
  {
    id: "burned",
    prop: "burned_buildings",
    fill: "#D32F2F",
    fillOpacity: 0.95,
    line: "#7A0F0F",
    lineWidth: 1,
    dasharray: null,
    dot: "#D32F2F",
  },
];

const names = (id) => ({
  source: `${PREFIX}-${id}`,
  fill: `${PREFIX}-${id}-fill`,
  line: `${PREFIX}-${id}-line`,
  dotSource: `${PREFIX}-${id}-dots`,
  dot: `${PREFIX}-${id}-dot`,
});

// every response key is a single MultiPolygon (buildings = one part per building),
// so dots are computed per part: the center of each part's bounding box
const polygonsOf = (fc) =>
  fc.features.flatMap((f) => {
    const g = f.geometry;
    if (g?.type === "Polygon") return [g.coordinates];
    if (g?.type === "MultiPolygon") return g.coordinates;
    return [];
  });

const dotsFrom = (fc) => ({
  type: "FeatureCollection",
  features: polygonsOf(fc).map((rings) => {
    let minX = Infinity;
    let minY = Infinity;
    let maxX = -Infinity;
    let maxY = -Infinity;
    rings[0].forEach(([x, y]) => {
      if (x < minX) minX = x;
      if (x > maxX) maxX = x;
      if (y < minY) minY = y;
      if (y > maxY) maxY = y;
    });
    return {
      type: "Feature",
      properties: {},
      geometry: {
        type: "Point",
        coordinates: [(minX + maxX) / 2, (minY + maxY) / 2],
      },
    };
  }),
});

export const clearViewedLayers = (map) => {
  SHAPES.forEach(({ id }) => {
    const n = names(id);
    [n.dot, n.line, n.fill].forEach((l) => {
      if (map.getLayer(l)) map.removeLayer(l);
    });
    [n.dotSource, n.source].forEach((s) => {
      if (map.getSource(s)) map.removeSource(s);
    });
  });
};

// data = raw response of GET /fire-analysis/{id}, or null to remove everything
export const syncViewedLayers = (map, data) => {
  clearViewedLayers(map);
  if (!data) return;

  SHAPES.forEach((s) => {
    const fc = data[s.prop];
    if (!fc?.features?.length) return;

    const n = names(s.id);
    map.addSource(n.source, { type: "geojson", data: fc });

    if (s.fillOpacity > 0) {
      map.addLayer({
        id: n.fill,
        type: "fill",
        source: n.source,
        paint: { "fill-color": s.fill, "fill-opacity": s.fillOpacity },
      });
    }

    map.addLayer({
      id: n.line,
      type: "line",
      source: n.source,
      paint: {
        "line-color": s.line,
        "line-width": s.lineWidth,
        ...(s.dasharray ? { "line-dasharray": s.dasharray } : {}),
      },
    });

    if (s.dot) {
      map.addSource(n.dotSource, { type: "geojson", data: dotsFrom(fc) });
      map.addLayer({
        id: n.dot,
        type: "symbol",
        source: n.dotSource,
        maxzoom: DOT_MAX_ZOOM,
        layout: {
          "text-field": "▼",
          "text-allow-overlap": true,
          "text-ignore-placement": true,
          "text-size": [
            "interpolate",
            ["linear"],
            ["zoom"],
            8,
            56,
            11,
            44,
            14,
            28,
            16,
            16,
          ],
        },
        paint: {
          "text-color": s.dot,
          "text-halo-color": "#ffffff",
          "text-halo-width": 1,
        },
      });
    }
  });
};
export const viewedCounts = (data) => ({
  burned: polygonsOf(data.burned_buildings ?? { features: [] }).length,
  saved: polygonsOf(data.saved_buildings ?? { features: [] }).length,
});
export const setViewedLayersVisibility = (map, visible) => {
  SHAPES.forEach(({ id }) => {
    const n = names(id);
    [n.fill, n.line, n.dot].forEach((layer) => {
      if (map.getLayer(layer)) {
        map.setLayoutProperty(
          layer,
          "visibility",
          visible[id] === false ? "none" : "visible",
        );
      }
    });
  });
};